import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import keycloak, { activeRealm } from "../keycloak";
import { ensureAccessToken } from "../keycloakRefresh";

const AuthContext = createContext(null);
const TOKEN_REFRESH_INTERVAL_MS = 60 * 1000;
// V1-1596: when the user is actively typing/clicking, refresh at most this often
// to keep the Keycloak SSO Session Idle clock from running out mid-data-entry.
const ACTIVITY_REFRESH_THROTTLE_MS = 30 * 1000;
const ACTIVITY_EVENTS = ["keydown", "mousedown", "touchstart", "input", "scroll"];
const TOKEN_STORAGE_KEY = "kc_tokens";

export function AuthProvider({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized,   setInitialized]   = useState(false);
  const [user,          setUser]           = useState(null);
  const refreshIntervalRef = useRef(null);
  const initCalledRef      = useRef(false);
  // V1-1596 — last time we refreshed the token in response to user activity.
  const lastActivityRefreshRef = useRef(0);

  function buildUser() {
    const p = keycloak.tokenParsed || {};
    return {
      id:        p.sub,
      username:  p.preferred_username,
      email:     p.email,
      firstName: p.given_name,
      lastName:  p.family_name,
      roles:     p.realm_access?.roles || [],
      role:      p.realm_access?.roles?.includes("platform-admin")
        ? "platform-admin"
        : p.realm_access?.roles?.includes("tenant-admin")
        ? "tenant-admin"
        : p.realm_access?.roles?.includes("enterprise-ai-owner")
        ? "enterprise-ai-owner"
        : "user",
    };
  }

  // ✅ Check if stored token is still valid (not expired)
  function isTokenValid(token) {
    try {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      // Check expiry with 30s buffer
      return payload.exp * 1000 > Date.now() + 30000;
    } catch {
      return false;
    }
  }

  // ✅ Called by Login.js + SSOCallback.js after getting tokens
  // Injects tokens into keycloak-js AND persists to sessionStorage
  function injectTokens({ access_token, refresh_token, id_token }) {
    keycloak.token         = access_token;
    keycloak.refreshToken  = refresh_token;
    keycloak.idToken       = id_token || "";
    keycloak.authenticated = true;
    keycloak.tokenParsed   = JSON.parse(
      atob(access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );

    // ✅ Persist tokens so reload doesn't log out
    sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
      access_token,
      refresh_token,
      id_token: id_token || "",
    }));

    setAuthenticated(true);
    setUser(buildUser());
  }

  // ✅ Restore tokens from sessionStorage on page reload
  function restoreTokensFromStorage() {
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) return false;

      const { access_token, refresh_token, id_token } = JSON.parse(stored);

      if (!access_token || !isTokenValid(access_token)) {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        return false;
      }

      keycloak.token         = access_token;
      keycloak.refreshToken  = refresh_token;
      keycloak.idToken       = id_token || "";
      keycloak.authenticated = true;
      keycloak.tokenParsed   = JSON.parse(
        atob(access_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
      );

      setAuthenticated(true);
      setUser(buildUser());
      return true;
    } catch {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      return false;
    }
  }

  useEffect(() => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;

    // ✅ Try restoring from storage FIRST before Keycloak init
    const restored = restoreTokensFromStorage();

    keycloak
      .init({
        onLoad:                    "check-sso",
        silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
        pkceMethod:                "S256",
        checkLoginIframe:          false,
      })
      .then((auth) => {
        if (auth) {
          // Keycloak SSO session found — use it
          setAuthenticated(true);
          setUser(buildUser());
        } else if (!restored && !keycloak.token) {
          // No SSO + no stored tokens + no freshly injected tokens → not authenticated
          setAuthenticated(false);
        }
        // If restored = true OR injectTokens ran during init → keep that state ✅
        setInitialized(true);
      })
      .catch((err) => {
        console.error("Keycloak init failed:", err);
        // Even if Keycloak fails, restored tokens are still valid
        setInitialized(true);
      });

    keycloak.onAuthSuccess = () => {
      setAuthenticated(true);
      setUser(buildUser());
    };

    keycloak.onAuthLogout = () => {
      setAuthenticated(false);
      setUser(null);
      sessionStorage.removeItem(TOKEN_STORAGE_KEY); // ✅ Clear on logout
    };

    keycloak.onTokenExpired = () => {
      ensureAccessToken(30)
        .then(() => {
          setUser(buildUser());
          sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
            access_token:  keycloak.token,
            refresh_token: keycloak.refreshToken,
            id_token:      keycloak.idToken || "",
          }));
        })
        .catch(() => {
          setAuthenticated(false);
          setUser(null);
          sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        });
    };

    const persistTokens = () => {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
        access_token:  keycloak.token,
        refresh_token: keycloak.refreshToken,
        id_token:      keycloak.idToken || "",
      }));
    };

    const refreshAndPersist = (minValidity) => {
      if (!keycloak.authenticated) return Promise.resolve();
      return ensureAccessToken(minValidity)
        .then(persistTokens)
        .catch(() => {
          setAuthenticated(false);
          setUser(null);
          sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        });
    };

    refreshIntervalRef.current = setInterval(() => {
      refreshAndPersist(60);
    }, TOKEN_REFRESH_INTERVAL_MS);

    // V1-1596: every active user gesture refreshes the token (throttled).
    // This extends the Keycloak SSO Session Idle clock while the user is
    // typing in chat / Gate-0 forms — they no longer get bumped out
    // mid-data-entry.
    const onActivity = () => {
      if (!keycloak.authenticated) return;
      const now = Date.now();
      if (now - lastActivityRefreshRef.current < ACTIVITY_REFRESH_THROTTLE_MS) return;
      lastActivityRefreshRef.current = now;
      refreshAndPersist(60);
    };
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, onActivity, { passive: true })
    );

    // When the tab regains focus, immediately top up the token so the next
    // request doesn't 401.
    const onVisibility = () => {
      if (document.visibilityState === "visible" && keycloak.authenticated) {
        lastActivityRefreshRef.current = Date.now();
        refreshAndPersist(60);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function login()  { keycloak.login(); }
  function logout() {
    const platformRealm = process.env.REACT_APP_KEYCLOAK_REALM || "validaite";
    if (activeRealm !== platformRealm) localStorage.removeItem("kc_realm");
    sessionStorage.removeItem(TOKEN_STORAGE_KEY); // ✅ Clear tokens on logout
    keycloak.logout({ redirectUri: window.location.origin });
  }
  function getToken()        { return keycloak.token || null; }
  function hasRole(role)     { return user?.roles?.includes(role) || false; }
  function hasAnyRole(roles) { return roles.some((r) => hasRole(r)); }

  return (
    <AuthContext.Provider value={{
      authenticated, initialized, user,
      login, logout, getToken, injectTokens,
      hasRole, hasAnyRole, activeRealm,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}